import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, LogIn, UserPlus } from "lucide-react";
import { toast } from "sonner";

// Combined Log in / Sign up dialog used from the Landing page. `mode` is
// "login" | "register"; onAuthed(user) fires after a successful auth.
export default function AuthDialog({ open, onOpenChange, mode = "login", onModeChange, onLogin, onRegister, onAuthed, onForgotPassword }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const isRegister = mode === "register";

  const reset = () => {
    setEmail("");
    setPassword("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error("Please enter your email and password.");
      return;
    }
    setLoading(true);
    try {
      const u = isRegister
        ? await onRegister(email.trim(), password)
        : await onLogin(email.trim(), password);
      toast.success(isRegister ? "Account created — welcome!" : "Welcome back!");
      reset();
      onOpenChange(false);
      onAuthed?.(u);
    } catch (err) {
      toast.error(err.response?.data?.detail || (isRegister ? "Registration failed." : "Login failed."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="auth-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isRegister ? <UserPlus className="w-5 h-5 text-primary" /> : <LogIn className="w-5 h-5 text-primary" />}
            {isRegister ? "Create your SalesTap account" : "Log in to SalesTap"}
          </DialogTitle>
          <DialogDescription>
            {isRegister
              ? "Register to connect your HubSpot. We only store your login — your CRM data stays read-only and ephemeral."
              : "Log in to connect your HubSpot and run your analysis."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label htmlFor="auth-email">Email</Label>
            <Input
              id="auth-email"
              data-testid="auth-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="auth-password">Password</Label>
            <Input
              id="auth-password"
              data-testid="auth-password"
              type="password"
              autoComplete={isRegister ? "new-password" : "current-password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={isRegister ? "At least 6 characters" : "Your password"}
            />
          </div>
          <Button type="submit" data-testid="auth-submit" disabled={loading} className="w-full">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {isRegister ? "Sign up" : "Log in"}
          </Button>
        </form>

        {!isRegister && onForgotPassword && (
          <p className="text-sm text-center -mt-1">
            <button
              type="button"
              data-testid="auth-forgot-password"
              onClick={onForgotPassword}
              className="text-primary font-medium hover:underline"
            >
              Forgot password?
            </button>
          </p>
        )}

        <p className="text-sm text-muted-foreground text-center mt-2">
          {isRegister ? "Already have an account?" : "New to SalesTap?"}{" "}
          <button
            type="button"
            data-testid="auth-toggle-mode"
            onClick={() => onModeChange(isRegister ? "login" : "register")}
            className="text-primary font-medium hover:underline"
          >
            {isRegister ? "Log in" : "Create an account"}
          </button>
        </p>
      </DialogContent>
    </Dialog>
  );
}
